/**
 * API call tracker — keeps:
 *   - the single most-recent FAILED call (used for "what just broke" in
 *     the bug-report metadata; this is the original bBUG1 behavior)
 *   - a ring buffer of the last 10 calls (success OR failure) used for
 *     the "Recent API History" section in the bBUG2 issue body
 *
 * Privacy: we keep method + URL (query-stripped) + status. bBUG3 ALSO
 * captures the response body on FAILED calls only (4xx/5xx) — the body
 * usually carries the actual error code/message engineers need to
 * triage from the GitHub issue. The body is:
 *   - capped at 2048 chars (truncation suffix added)
 *   - sanitized to strip Bearer tokens, JWT prefixes, and password=...
 *   - NEVER captured for 2xx responses (would explode the issue body)
 *
 * The file name stays `failed-api-tracker` even though the scope grew,
 * to avoid churning a ton of unrelated imports across the web package.
 *
 * Wired from `api-client.ts`:
 *   - `recordApiCall` is called on EVERY non-network-error response
 *   - `recordFailedApiCall` is the legacy alias for callers that only
 *     want the "what just broke" surface
 */

/** Max chars of the response body we keep. 2 KB is enough for typical
 *  error envelopes (`{ "error": { "code": "X", "message": "Y" } }`) and
 *  small enough that 10 entries × 2 KB stays well within GitHub's 65 KB
 *  issue-body cap with room for the rest of the metadata. */
const MAX_RESPONSE_BODY_CHARS = 2048;

export interface LastFailedApi {
  method: string;
  /** URL stripped of query string to reduce accidental data leakage. */
  url: string;
  status: number;
  timestamp: string;
  /**
   * Best-effort sanitized response body. Present only on captured
   * failures. Truncated to 2 KB with `...[truncated, N more bytes]`
   * suffix when the original exceeded the cap.
   */
  responseBody?: string;
}

/** Ring buffer entry (compact shape — distinct from LastFailedApi). */
export interface ApiCallRecord {
  ts: string;
  method: string;
  path: string;
  status: number;
  /** Same sanitize+truncate as LastFailedApi.responseBody. Only set on
   *  non-2xx entries to keep the ring's serialized footprint bounded. */
  responseBody?: string;
}

const RING_SIZE = 10;
let last: LastFailedApi | null = null;
const ring: ApiCallRecord[] = [];

function stripQuery(url: string): string {
  // Allow absolute paths and full URLs both. Strip the search string.
  const q = url.indexOf('?');
  return q >= 0 ? url.slice(0, q) : url;
}

/**
 * Reduce an absolute URL to a relative path. The bug-report metadata
 * blob is verbose enough already; full hostnames waste characters of
 * the 60 KB issue-body budget without adding signal.
 */
function urlToPath(url: string): string {
  const stripped = stripQuery(url);
  try {
    const parsed = new URL(stripped, 'http://placeholder.invalid');
    return parsed.pathname || stripped;
  } catch {
    return stripped;
  }
}

/**
 * bBUG3 — sanitize a response body before capture. Best-effort; perfect
 * sanitization is impossible (and unnecessary — we own the API surface).
 * The goal is to scrub the OBVIOUS shapes that would leak credentials
 * if the bug-report ended up in an external issue tracker:
 *
 *   - `Bearer <token>` — Authorization headers occasionally echoed back
 *   - `eyJ...` — JWT prefix; common in 401 responses that include the
 *     decoded token claims for diagnostics
 *   - `password='...'` / `password: "..."` — login-failure payloads
 *
 * Regexes run in `gi` mode so multiple occurrences are caught. Each
 * replacement emits a fixed `[REDACTED]` marker so triagers can see
 * that scrubbing happened (rather than wondering why a token is missing).
 */
function sanitizeResponseBody(raw: string): string {
  let out = raw;
  // Bearer tokens (rfc6750)
  out = out.replace(/Bearer\s+[A-Za-z0-9._\-]+/gi, 'Bearer [REDACTED]');
  // JWT (header.payload.signature) — typical prefix `eyJ`; tolerate the
  // full three-segment shape and standalone payloads alike.
  out = out.replace(/eyJ[A-Za-z0-9._\-]+/g, '[REDACTED_JWT]');
  // password=value / password: 'value' / "password":"value"
  out = out.replace(
    /password['"]?\s*[:=]\s*['"]?[^'"\s,}]+/gi,
    'password=[REDACTED]',
  );
  return out;
}

/**
 * bBUG3 — truncate to MAX_RESPONSE_BODY_CHARS with a suffix that names
 * the dropped byte count. Engineers reading the issue need to know
 * whether the body was complete or whether they need to dig into the
 * server logs for the full payload.
 */
function truncateResponseBody(raw: string): string {
  if (raw.length <= MAX_RESPONSE_BODY_CHARS) return raw;
  const head = raw.slice(0, MAX_RESPONSE_BODY_CHARS);
  const dropped = raw.length - MAX_RESPONSE_BODY_CHARS;
  return `${head}...[truncated, ${dropped} more bytes]`;
}

/**
 * Record ANY API call observed by the api-client (success or failure).
 * Also forwards to the legacy "last failed" buffer when status >= 400.
 *
 * bBUG3: callers MAY pass `responseBody` (already-read text from a
 * `response.clone().text()` call). When status < 400 the body is
 * dropped (2xx happy-path bodies waste budget). When status >= 400 the
 * body is sanitized + truncated, then stored on the ring entry AND on
 * the "last failed" buffer.
 */
export function recordApiCall(opts: {
  method: string;
  url: string;
  status: number;
  /** Best-effort response body. Only retained on non-2xx entries. */
  responseBody?: string;
}): void {
  const entry: ApiCallRecord = {
    ts: new Date().toISOString(),
    method: opts.method.toUpperCase(),
    path: urlToPath(opts.url),
    status: opts.status,
  };
  if (opts.status >= 400 && typeof opts.responseBody === 'string') {
    entry.responseBody = truncateResponseBody(
      sanitizeResponseBody(opts.responseBody),
    );
  }
  ring.push(entry);
  while (ring.length > RING_SIZE) ring.shift();
  if (opts.status >= 400) {
    last = {
      method: entry.method,
      url: entry.path,
      status: opts.status,
      timestamp: entry.ts,
      // Re-use the same already-processed body so we don't sanitize twice.
      ...(entry.responseBody !== undefined
        ? { responseBody: entry.responseBody }
        : {}),
    };
  }
}

/**
 * Legacy entry point — keeps existing call sites in api-client.ts
 * compiling unchanged. Internally just forwards to `recordApiCall`.
 */
export function recordFailedApiCall(opts: {
  method: string;
  url: string;
  status: number;
  responseBody?: string;
}): void {
  recordApiCall(opts);
}

/** Returns the last failed API call, or null if none was observed. */
export function getLastFailedApiCall(): LastFailedApi | null {
  return last;
}

/**
 * Returns the most-recent API calls (success or failure), chronological
 * order (oldest first). Up to 10 entries. Used by the bug-report
 * metadata helper.
 */
export function getLastApiCalls(): ApiCallRecord[] {
  return ring.slice();
}

/** Test-only: clear all internal buffers. */
export function _resetFailedApiTracker(): void {
  last = null;
  ring.length = 0;
}
